import { getJson, postJson } from "../../utils/api";
import { isCountryUk } from "../../utils/country";

export enum PlanInterval {
  day = "day",
  week = "week",
  month = "month",
  year = "year",
}
export enum AnnualRenewalPeriod {
  annually = 1,
  biannually = 2,
}
export enum MonthlyRenewalPeriod {
  monthly = 1,
  q2month = 2,
  q3month = 3,
}

export interface SubscriptionPlan {
  subscriptionPlanId: number;
  stripePlanId: string;
  name: string;
  title: string;
  subtitle: string;
  availability?: string;
  amount: number;
  currency: string;
  renewalPeriod: MonthlyRenewalPeriod | AnnualRenewalPeriod;
  interval: PlanInterval;
  creditAmount: number;
  order: number;
  isDefault: boolean;
}

export interface Pocket {
  id: number;
  name: string;
  url: string;
  balance: number;
  status: string;
  type: string;
  accountNumber: string;
  sortCode: string;
}

export interface CreditHistoryItem {
  amount: number;
  createdAt: string;
  creditAmount: number;
  prevAmount: number;
  prevCreditAmount: number;
  reportedAt: string;
}

export interface CreditHistoryResponse {
  items: CreditHistoryItem[];
  limit: number;
  offset: number;
  total: number;
}

export class SubscriptionApi {
  /**
   * Subscription plans.
   *
   * The two regions serve these from different services, in different
   * shapes, so this cannot be one call:
   *
   *  - UK: the payment API, as `{ data: { plans: [...] } }`.
   *  - US: the main API, as `{ items: [...] }`. The US payment API does
   *    answer `/plans`, but with an empty list, so pointing the US at it
   *    yields no plans at all and the plan screens throw "No active
   *    subscription plans". Its items also carry the plan's own id as `id`,
   *    where the UK payload and everything downstream use
   *    `subscriptionPlanId`, so it is renamed here. Without that every US
   *    plan has an undefined id, `undefined === undefined` marks all of them
   *    selected at once, every card's button is enabled, and the pre-selected
   *    default plan is what gets submitted whichever card the user chose.
   *
   * Falls back to an empty array rather than `undefined`: every caller goes
   * straight into `.filter` or `.sort` on what comes back.
   */
  async getSubscriptionPlans(): Promise<SubscriptionPlan[]> {
    if (isCountryUk()) {
      return await getJson("plans", process.env.REACT_APP_BASE_PAYMENT_API_URL)
        .then((response) => response.json())
        .then((response) => response["data"]?.["plans"] ?? []);
    }

    return await getJson("plans")
      .then((response) => response.json())
      .then((response) =>
        (response["items"] ?? []).map(
          (item: Omit<SubscriptionPlan, "subscriptionPlanId"> & { id: number }): SubscriptionPlan => ({
            ...item,
            subscriptionPlanId: item.id,
          })
        )
      );
  }

  async updateSubscriptionPlans(planId: number): Promise<Pocket> {
    return postJson(`wallet/subscriptions/plan`, { planId }, process.env.REACT_APP_BASE_PAYMENT_API_URL).then(
      (response) => response.json()
    );
  }

  async createPocket(): Promise<Pocket> {
    return (await postJson(`pockets?wait=true`, { type: "CreditReport" })).json();
  }

  async getPockets(): Promise<Pocket[]> {
    return await getJson(`pockets?limit=100`)
      .then((response) => response.json())
      .then((response) => response["items"]);
  }

  async getCreditHistory(pocketId: number, limit: number, offset: number): Promise<CreditHistoryResponse> {
    return await getJson(`pockets/${pocketId}/credit_history?limit=${limit}&offset=${offset}`).then((response) =>
      response.json()
    );
  }

  async getOrCreatePocket(): Promise<Pocket> {
    const pockets = await this.getPockets();
    if (pockets.length === 0) {
      return await this.createPocket();
    }
    return pockets[0];
  }
}
